import { useState, useCallback, useRef, useEffect } from "react";
import debounce from "lodash/debounce";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";

interface AddressResult {
  address: string;
  id: string;
}

async function fetchSuggestions(input: string): Promise<AddressResult[]> {
  if (!input || input.length < 3) return [];

  const response = await fetch(
    `https://api.psma.com.au/v1/predictive-demo/address?query=${encodeURIComponent(input)}`,
  );
  if (response.status !== 200) return [];

  const body = await response.json();
  return body.suggest.map((s: any) => ({
    address: s.address,
    id: s.id,
  }));
}

interface PredictiveAddressInputProps {
  id?: string;
  name?: string;
  value: string;
  disabled?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  error?: boolean;
  helperText?: string | false;
  onAddressSelect: (address: string, addressId: string) => void;
  onChange?: (value: string) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onClear?: () => void;
}

export function PredictiveAddressInput({
  id,
  name,
  value,
  disabled = false,
  readOnly = false,
  autoFocus = false,
  placeholder = "Start typing an address...",
  className,
  error,
  helperText,
  onAddressSelect,
  onChange,
  onBlur,
  onClear,
}: PredictiveAddressInputProps) {
  const [suggestions, setSuggestions] = useState<AddressResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const listRef = useRef<HTMLDivElement>(null);

  const debouncedFetch = useRef(
    debounce(async (input: string) => {
      setLoading(true);
      try {
        const results = await fetchSuggestions(input);
        setSuggestions(results);
        setIsManualAddress(results.length === 0 && input.length >= 3);
        setOpen(results.length > 0);
        setHighlightedIndex(-1);
      } finally {
        setLoading(false);
      }
    }, 300),
  ).current;

  useEffect(() => {
    return () => debouncedFetch.cancel();
  }, [debouncedFetch]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const nextValue = e.target.value;
      onChange?.(nextValue);
      if (nextValue.length >= 3) {
        debouncedFetch(nextValue);
      } else {
        setSuggestions([]);
        setOpen(false);
      }
    },
    [onChange, debouncedFetch],
  );

  const selectSuggestion = useCallback(
    (suggestion: AddressResult) => {
      onAddressSelect(suggestion.address, suggestion.id);
      setOpen(false);
      setSuggestions([]);
    },
    [onAddressSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open || suggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0,
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1,
          );
          break;
        case "Enter":
          e.preventDefault();
          if (highlightedIndex >= 0) {
            selectSuggestion(suggestions[highlightedIndex]);
          }
          break;
        case "Escape":
          setOpen(false);
          break;
      }
    },
    [open, suggestions, highlightedIndex, selectSuggestion],
  );

  return (
    <div className={cn("relative w-full", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div className="relative">
            <Input
              id={id}
              name={name}
              value={value}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={readOnly}
              autoFocus={autoFocus}
              autoComplete="off"
              aria-invalid={error || undefined}
              aria-expanded={open}
              aria-autocomplete="list"
              role="combobox"
              className={cn(
                readOnly && "opacity-100 cursor-default",
                value && !disabled && !readOnly && "pr-8",
              )}
              onChange={handleInputChange}
              onBlur={(e) => {
                setTimeout(() => setOpen(false), 200);
                onBlur?.(e);
              }}
              onFocus={() => {
                if (suggestions.length > 0) setOpen(true);
              }}
              onKeyDown={handleKeyDown}
            />
            {loading ? (
              <div className="absolute top-1/2 right-2 -translate-y-1/2">
                <Spinner className="size-3.5" />
              </div>
            ) : (
              value &&
              !disabled &&
              !readOnly && (
                <button
                  type="button"
                  tabIndex={-1}
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2"
                  onClick={() => {
                    onChange?.("");
                    onClear?.();
                    setSuggestions([]);
                    setOpen(false);
                    setIsManualAddress(false);
                  }}
                >
                  <XIcon className="size-3.5" />
                </button>
              )
            )}
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="w-(--radix-popover-trigger-width) p-1"
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => e.preventDefault()}
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <div
            ref={listRef}
            role="listbox"
            className="max-h-60 overflow-y-auto"
          >
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.id}
                role="option"
                aria-selected={index === highlightedIndex}
                data-highlighted={index === highlightedIndex || undefined}
                className="data-highlighted:bg-accent data-highlighted:text-accent-foreground cursor-default rounded-md px-2 py-1.5 text-xs/relaxed select-none"
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectSuggestion(suggestion);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {suggestion.address}
              </div>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {isManualAddress && (
        <p className="text-muted-foreground mt-1 text-xs">
          Address entered manually
        </p>
      )}
      {helperText && (
        <p
          className={cn(
            "mt-1 text-xs",
            error ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {helperText}
        </p>
      )}
    </div>
  );
}
